import { WorkingHoursScheduleType } from '../visits/schedule/entity';

export type User = {
  id: string;
  name: string;
  email: string;
  authProvider?: string | null;
  createdAt: Date;
  lastLogin?: Date | null;
} & HasLoyaltyPercent;

export type HasLoyaltyPercent = { loyaltyPercent?: number };

export type UserForm = {
  contactEmail?: string | null;
  name?: string;
  bankAccount?: string;
  reviewUrl?: string;
  colorScheme?: string;
  workingSchedule?: WorkingHoursScheduleType;
  hasSeenNews?: boolean;
  avatarUrl?: string;
  publicId?: string;
  address?: string;
  bio?: string;
  phone?: string;
  allowPublicReservations?: boolean;
  preferredCurrency?: string;
} & HasLoyaltyPercent;

export type UserCreateData = {
  id: string;
  name: string;
  email: string;
  authProvider?: string;
  lastLogin?: Date;
};

export type UserId = { userId: string };

export type WithUserId<TData> = TData & UserId;

export const generateBankAlias = (userId: string | undefined | null): string | undefined => {
  if (!userId) {
    return undefined;
  }

  const firstThree = userId.slice(0, 3);
  const lastSixDigits = (userId.match(/\d/g) ?? []).join('').slice(-6);

  return `platby+${firstThree}${lastSixDigits}@kadernikplus.cz`;
};

/** Číselník českých bank podle kódu banky (poslední čtyřčíslí čísla účtu). */
export const CZECH_BANKS: Record<string, string> = {
  '0100': 'KB',
  '0300': 'ČSOB',
  '0600': 'MONETA Money Bank',
  '0710': 'Česká národní banka',
  '0800': 'Česká spořitelna',
  '2010': 'Fio banka',
  '2060': 'Citfin',
  '2070': 'Trinity Bank',
  '2250': 'Banka CREDITAS',
  '2260': 'NEY spořitelní družstvo',
  '2600': 'Citibank',
  '2700': 'UniCredit Bank',
  '3030': 'Air Bank',
  '3050': 'BNP Paribas Personal Finance',
  '3060': 'PKO BP',
  '3500': 'ING Bank',
  '4000': 'Max banka',
  '4300': 'Národní rozvojová banka',
  '5500': 'Raiffeisenbank',
  '5800': 'J&T Banka',
  '6000': 'PPF banka',
  '6100': 'Raiffeisenbank (Equa bank)',
  '6200': 'COMMERZBANK',
  '6210': 'mBank',
  '6300': 'BNP Paribas',
  '6700': 'Všeobecná úverová banka',
  '6800': 'Sberbank CZ',
  '7910': 'Deutsche Bank',
  '7950': 'Raiffeisen stavební spořitelna',
  '7960': 'ČSOB stavební spořitelna',
  '7970': 'MONETA stavební spořitelna',
  '7990': 'Modrá pyramida stavební spořitelna',
  '8030': 'Volksbank Raiffeisenbank',
  '8040': 'Oberbank',
  '8060': 'Stavební spořitelna České spořitelny',
  '8090': 'Česká exportní banka',
  '8150': 'HSBC',
  '8190': 'Sparkasse',
  '8255': 'Bank of China',
  '8265': 'Industrial and Commercial Bank of China',
};

/**
 * Kódy bank, pro které je aktuálně podporováno automatické párování
 * příchozích plateb (bankovní e-mailové notifikace).
 */
export const SUPPORTED_PAIRING_BANK_CODES = ['3030', '0300', '0100', '5500'] as const;

/**
 * Banky, pro které máme v aplikaci dostupný návod na nastavení párování
 * plateb + logo pro zobrazení v chipu.
 */
export const PAIRING_TUTORIAL_BANK_CODES = [
  '3030', // Air Bank
  '0300', // ČSOB
  '0100', // KB
  '5500', // Raiffeisenbank
  '2010', // Fio banka
  '2700', // UniCredit Bank
] as const;

/** Loga bank (soubory v `public/bank-tutorial`). */
export const SUPPORTED_PAIRING_BANK_LOGOS: Record<string, string> = {
  '3030': '/bank-tutorial/airbank.png',
  '0300': '/bank-tutorial/csob.png',
  '0100': '/bank-tutorial/kb.png',
  '5500': '/bank-tutorial/raiffeisenbank.png',
  '2010': '/bank-tutorial/fio.jpeg',
  '2700': '/bank-tutorial/unicreditbank.jpeg',
};

/** PDF návody k nastavení párování plateb v konkrétní bance. */
export const SUPPORTED_PAIRING_BANK_TUTORIALS: Record<string, string> = {
  '3030': '/bank-tutorial/airbank_tutorial.pdf',
  '0300': '/bank-tutorial/csob-tutorial.pdf',
  '0100': '/bank-tutorial/kb_tutorial.pdf',
  '5500': '/bank-tutorial/raiffeisenbank_tutorial.pdf',
  '2010': '/bank-tutorial/fio_tutorial.pdf',
  '2700': '/bank-tutorial/unicreditbank_tutorial.pdf',
};

/** Podporovaná banka pro zobrazení v UI (chip s logem a názvem). */
export type SupportedPairingBank = {
  code: string;
  name: string;
  logo?: string;
  tutorialUrl?: string;
};

/** Seznam podporovaných bank s názvem a logem pro vykreslení chipů. */
export const supportedPairingBanks = (): SupportedPairingBank[] =>
  PAIRING_TUTORIAL_BANK_CODES.map(code => ({
    code,
    name: CZECH_BANKS[code] ?? code,
    logo: SUPPORTED_PAIRING_BANK_LOGOS[code],
    tutorialUrl: SUPPORTED_PAIRING_BANK_TUTORIALS[code],
  }));

/** Lidsky čitelný seznam podporovaných bank, např. „Air Bank, ČSOB". */
export const supportedPairingBankNames = (): string =>
  SUPPORTED_PAIRING_BANK_CODES.map(code => CZECH_BANKS[code] ?? code).join(', ');

/**
 * Vytáhne kód banky (poslední čtyřčíslí za lomítkem) z čísla účtu.
 * Podporuje formát `[předčíslí-]číslo/kód`. Vrací null, pokud kód není zadán.
 */
export const parseBankCode = (account: string | undefined | null): string | null => {
  if (!account) {
    return null;
  }
  const match = account.replace(/\s+/g, '').match(/\/(\d{4})$/);
  return match ? match[1] : null;
};

/** Vrací true, pokud kód banky podporuje automatické párování plateb. */
export const isSupportedPairingBankCode = (code: string | null): boolean =>
  code != null && (SUPPORTED_PAIRING_BANK_CODES as readonly string[]).includes(code);
